import type { IPaginatedResponse, IUserLookup, IUserSummary } from '@pulse/shared';

import { apiFetch } from '../../../lib/api-client';
import { extractErrorMessage } from '../../../lib/api-error.util';

const BASE = '/api/v1/users';

// Full flat list, including roles/permissions/isActive/lastLoginAt — requires admin:read.
// Used only by admin-only screens (Users/Roles list pages, and the handful of other admin-only
// Head/Owner pickers on Departments/Business Units/Sales Regions/Approver groups). Every
// project-domain EntityPicker field (Owner/Assignee/Lead/Manager/Escalated-To) should use
// fetchUserLookup() below instead — see its own comment for why.
export async function fetchUsers(): Promise<IUserSummary[]> {
  const res = await apiFetch(BASE);
  if (!res.ok) throw new Error(await extractErrorMessage(res, `Failed to fetch users: ${res.status}`));
  return res.json() as Promise<IUserSummary[]>;
}

// Minimal active-user list — the shared data source for every project-domain EntityPicker field
// (Owner/Assignee/Lead/Manager/Escalated-To across delivery/presales). Backed by
// GET /users/lookup, which only requires project:read (not admin:read) since populating these
// pickers is an ordinary part of using the Delivery/Pre-Sales modules — see
// UserController.findAllLookup()'s own comment on the API side. Deliberately narrower than
// fetchUsers(): excludes deactivated users and only carries id/name/email/avatarUrl (the IUserRef
// shape every Owner/Assignee/Lead/Manager field on Project/Issue/Risk/Escalation/Dependency/
// ChangeRequest already uses) plus weeklyCapacityHours, for Resource Allocation — see IUserLookup.
export async function fetchUserLookup(): Promise<IUserLookup[]> {
  const res = await apiFetch(`${BASE}/lookup`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, `Failed to fetch users: ${res.status}`));
  return res.json() as Promise<IUserLookup[]>;
}

export interface UserListFilters {
  search?: string;
  isActive?: boolean;
  limit?: number;
  offset?: number;
}

// Server-side paginated list — used only by UsersPage (the admin list page).
export async function fetchUsersPaginated(filters: UserListFilters = {}): Promise<IPaginatedResponse<IUserSummary>> {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.isActive !== undefined) params.set('isActive', String(filters.isActive));
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.offset !== undefined) params.set('offset', String(filters.offset));

  const res = await apiFetch(`${BASE}/paginated?${params.toString()}`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, `Failed to fetch users: ${res.status}`));
  return res.json() as Promise<IPaginatedResponse<IUserSummary>>;
}

export async function updateUserRoles(id: string, roleIds: string[]): Promise<IUserSummary> {
  const res = await apiFetch(`${BASE}/${id}/roles`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roleIds }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update roles'));
  return res.json() as Promise<IUserSummary>;
}

export async function updateUserCapacity(id: string, weeklyCapacityHours: number): Promise<IUserSummary> {
  const res = await apiFetch(`${BASE}/${id}/capacity`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ weeklyCapacityHours }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update weekly capacity'));
  return res.json() as Promise<IUserSummary>;
}

export async function updateUserOrganization(
  id: string,
  data: { isManagement: boolean; businessUnitId?: string | null },
): Promise<IUserSummary> {
  const res = await apiFetch(`${BASE}/${id}/organization`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update Business Unit / Management'));
  return res.json() as Promise<IUserSummary>;
}

export async function updateUserContact(id: string, contact: string | null): Promise<IUserSummary> {
  const res = await apiFetch(`${BASE}/${id}/contact`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contact }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update contact'));
  return res.json() as Promise<IUserSummary>;
}

export async function deactivateUser(id: string): Promise<IUserSummary> {
  const res = await apiFetch(`${BASE}/${id}/deactivate`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to deactivate user'));
  return res.json() as Promise<IUserSummary>;
}

export async function reactivateUser(id: string): Promise<IUserSummary> {
  const res = await apiFetch(`${BASE}/${id}/reactivate`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to reactivate user'));
  return res.json() as Promise<IUserSummary>;
}
